import { useState, useEffect } from "react";
import DateInput from "@/components/DateInput";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ClienteCombobox from "@/components/ClienteCombobox";
import { Trash, Plus, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import NumberInput from "@/components/ui/NumberInput";

const nuevoItem = () => ({ cod_tipo_prod: "", descripcion: "", kilos: 0, unidades: 0, tropa: "" });

const initialForm = {
  especie: "bovino",
  tipo_movimiento: "ENV",
  cod_dom_origen: "",
  cod_dom_destino: "",
  categoria_emisor: 3, // Abastecedor (default)
  tipo_receptor: "MI", // Movimiento Interno (default)
  categoria_receptor: "",
  cliente_id: "",
  transportista_id: "",
  vehiculo_id: "",
  fecha_inicio_viaje: new Date().toISOString().slice(0, 10),
  distancia_km: 0,
  mercaderias: [nuevoItem()],
  observaciones: "",
  punto_emision: "",
};export default function EmitirRECDialog({ open, onOpenChange, config, onSuccess, factura = null, onCombinedSubmit = null }) {
  const [form, setForm] = useState(initialForm);
  const [clientes, setClientes] = useState([]);
  const [transportistas, setTransportistas] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [gruposCarne, setGruposCarne] = useState([]);
  const [tiposCarne, setTiposCarne] = useState([]);
  const [categoriasEmisor, setCategoriasEmisor] = useState([]);
  const [categoriasReceptor, setCategoriasReceptor] = useState([]);
  const [domsOrigen, setDomsOrigen] = useState([]);
  const [domsDestino, setDomsDestino] = useState([]);
  const [domsDestinoLoading, setDomsDestinoLoading] = useState(false);
  const [domsDestinoCuit, setDomsDestinoCuit] = useState("");
  const [emitting, setEmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [puntosEmision, setPuntosEmision] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const especieInit = factura ? (form.especie || "bovino") : form.especie;
      const [{ data: cs }, { data: ts }, { data: vs }, { data: gc }, { data: ce }, { data: cr }, { data: doms }, { data: pes }, { data: prods }, { data: tps }] =
        await Promise.all([
          api.get("/clientes"),
          api.get("/rec/transportistas"),
          api.get("/rec/vehiculos"),
          api.get("/rec/catalog/grupos-carne"),
          api.get("/rec/catalog/categorias-emisor"),
          api.get("/rec/catalog/categorias-receptor"),
          api.get("/rec/catalog/domicilios-origen"),
          api.get("/rec/puntos-emision"),
          api.get("/productos"),
          api.get(`/rec/catalog/tipos-por-especie?especie=${especieInit}`),
        ]);
      setClientes(cs || []);
      setTransportistas(ts || []);
      setVehiculos(vs || []);
      setGruposCarne(gc || []);
      setCategoriasEmisor(ce || []);
      setCategoriasReceptor(cr || []);
      setDomsOrigen(doms || []);
      setTiposCarne(tps || []);
      const activos = (pes || []).filter((p) => p.activo);
      setPuntosEmision(activos);
      const def = activos.find((p) => p.es_default) || activos[0];

      // Si viene con factura, prellenar cliente, fecha e items.
      setForm((f) => {
        const next = {
          ...f,
          cliente_id: factura ? factura.contraparte_id : f.cliente_id,
          fecha_inicio_viaje: factura?.fecha || f.fecha_inicio_viaje,
          punto_emision: f.punto_emision || (def ? String(def.numero) : ""),
        };
        // Cuando hay factura, siempre reconstruimos las mercaderías desde cero para evitar
        // que queden filas viejas cacheadas de una apertura previa.
        if (factura) {
          const prodByCodigo = new Map((prods || []).map((p) => [p.codigo, p]));
          const prodById = new Map((prods || []).map((p) => [p.id, p]));
          // Normaliza para matching flexible por nombre (sin acentos, minúsculas, sólo alfanumérico).
          const norm = (s) => (s || "").toString().toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9]/g, "");
          // Cache: nombre normalizado del producto/ítem → code SENASA
          const findSenasaByNombre = (nombre) => {
            const nq = norm(nombre);
            if (!nq || !(tps || []).length) return "";
            // 1) Coincidencia exacta por label normalizado
            let hit = (tps || []).find((tc) => norm(tc.label) === nq);
            // 2) Inclusión bidireccional (nombre del producto ⊆ label AFIP o viceversa)
            if (!hit) {
              hit = (tps || []).find((tc) => {
                const nL = norm(tc.label);
                return nL && (nL.includes(nq) || nq.includes(nL));
              });
            }
            return hit?.code ? String(hit.code) : "";
          };

          const mapped = (factura.items || []).map((it) => {
            const prod = (it.producto_id && prodById.get(it.producto_id))
                       || (it.codigo && prodByCodigo.get(it.codigo))
                       || null;
            // Prioridad: codigo_afip del producto > fuzzy match por nombre producto > fuzzy por descripcion del ítem
            const cod = (prod?.codigo_afip && String(prod.codigo_afip))
                     || findSenasaByNombre(prod?.nombre)
                     || findSenasaByNombre(it.descripcion)
                     || "";
            const kilos = Number(it.kilos) || 0;
            const cantidad = Number(it.cantidad) || 0;
            return {
              cod_tipo_prod: cod,
              descripcion: it.descripcion || prod?.nombre || "",
              kilos: kilos || cantidad,  // fallback: si no hay kilos, usar cantidad
              unidades: kilos > 0 && cantidad > 0 ? cantidad : 0,
              tropa: "",
            };
          }).filter((m) => Number(m.kilos) > 0 || Number(m.unidades) > 0);
          next.mercaderias = mapped.length > 0 ? mapped : [nuevoItem()];
        }
        return next;
      });
    })();
    // form.especie deliberadamente excluido: usamos el valor inicial y luego
    // el segundo useEffect refresca los tipos si el usuario cambia especie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, factura]);

  // Cargar tipos de carne según especie (bovino=todos los grupos 1-12+99, porcino=13+99)
  useEffect(() => {
    if (!open) return;
    api.get(`/rec/catalog/tipos-por-especie?especie=${form.especie}`)
      .then(({ data }) => setTiposCarne(data || []))
      .catch(() => setTiposCarne([]));
  }, [open, form.especie]);

  // Al cambiar cliente, cargar sus domicilios habilitados en SENASA
  useEffect(() => {
    if (!form.cliente_id) { setDomsDestino([]); setDomsDestinoCuit(""); return; }
    const cli = clientes.find((c) => c.id === form.cliente_id);
    const cuitClean = (cli?.cuit || "").replace(/\D/g, "");
    if (cuitClean.length !== 11) {
      setDomsDestino([]);
      setDomsDestinoCuit("invalid");
      return;
    }
    setDomsDestinoLoading(true);
    setDomsDestinoCuit(cuitClean);
    api.get(`/rec/catalog/domicilios-receptor?cuit=${cuitClean}`)
      .then(({ data }) => setDomsDestino(data || []))
      .catch(() => setDomsDestino([]))
      .finally(() => setDomsDestinoLoading(false));
  }, [form.cliente_id, clientes]);

  const vehiculosFiltrados = form.transportista_id
    ? vehiculos.filter((v) => v.transportista_id === form.transportista_id || !v.transportista_id)
    : vehiculos;

  const updateItem = (idx, patch) => {
    // Aplicar múltiples cambios en un solo setForm para evitar que un setForm
    // sobreescriba al anterior cuando se actualizan varios campos a la vez.
    const items = [...form.mercaderias];
    items[idx] = { ...items[idx], ...patch };
    setForm({ ...form, mercaderias: items });
  };
  const addItem = () => setForm({ ...form, mercaderias: [...form.mercaderias, nuevoItem()] });
  const removeItem = (idx) => setForm({ ...form, mercaderias: form.mercaderias.filter((_, i) => i !== idx) });

  const totalKilos = form.mercaderias.reduce((s, m) => s + (parseFloat(m.kilos) || 0), 0);

  const buildPayload = () => ({
    ...form,
    cod_dom_origen: form.cod_dom_origen ? Number(form.cod_dom_origen) : null,
    cod_dom_destino: Number(form.cod_dom_destino),
    categoria_emisor: Number(form.categoria_emisor || 1),
    categoria_receptor: form.categoria_receptor ? Number(form.categoria_receptor) : null,
    distancia_km: Number(form.distancia_km || 0),
    punto_emision: form.punto_emision ? Number(form.punto_emision) : null,
    mercaderias: form.mercaderias.map((m, i) => ({
      orden: i + 1,
      cod_tipo_prod: Number(m.cod_tipo_prod),
      descripcion: m.descripcion || null,
      kilos: Number(m.kilos),
      unidades: Number(m.unidades || 0),
      tropa: m.tropa ? Number(m.tropa) : null,
    })),
    factura_id: factura?.id || null,
  });

  const preview = async () => {
    if (!form.cliente_id || !form.transportista_id || !form.vehiculo_id) {
      toast.error("Completá cliente, transportista y vehículo para previsualizar");
      return;
    }
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/rec/preview-xml", buildPayload());
      setPreviewData(data);
      setPreviewOpen(true);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setPreviewLoading(false);
    }
  };

  const emitir = async () => {
    // validaciones
    if (!form.cliente_id) { toast.error("Seleccioná un cliente"); return; }
    if (!form.transportista_id) { toast.error("Seleccioná un transportista"); return; }
    if (!form.vehiculo_id) { toast.error("Seleccioná un vehículo"); return; }
    if (!form.punto_emision) { toast.error("Seleccioná un punto de emisión"); return; }
    if (!form.cod_dom_destino) { toast.error("Domicilio de destino requerido"); return; }
    if (form.mercaderias.some((m) => !m.cod_tipo_prod || !m.kilos)) {
      toast.error("Completá código de producto y kilos en todas las mercaderías");
      return;
    }

    setEmitting(true);
    try {
      const payload = buildPayload();
      if (onCombinedSubmit) {
        // Flujo combinado: el padre se encarga de llamar a /facturas/con-rec
        await onCombinedSubmit(payload);
      } else {
        const { data } = await api.post("/rec/emitir", payload, { timeout: 180000 });
        toast.success(`REC ${data.numero_formateado} emitido en AFIP`);
        setForm(initialForm);
        onSuccess?.(data);
      }
    } catch (e) {
      // Detectar caso de Cloudflare 502 / respuesta interrumpida por proxy.
      // El REC puede haberse emitido en AFIP igual — intentamos reconciliar.
      const status = e.response?.status;
      const proxyCorto = !e.response || status === 502 || status === 504 || /Cloudflare|origin web server/i.test(e.response?.data || "");
      if (proxyCorto) {
        toast.message("La respuesta del servidor se interrumpió. Consultando a AFIP…", { duration: 6000 });
        try {
          const pe = form.punto_emision || 0;
          const { data: imp } = await api.post(
            `/rec/importar-desde-afip?punto_emision=${pe}`,
            null,
            { timeout: 60000 },
          );
          if (imp.nuevos > 0) {
            toast.success(`AFIP emitió el REC igual. Reconciliado: ${imp.nuevos} nuevo(s)`);
            setForm(initialForm);
            onSuccess?.(null);
            return;
          }
          toast.error("El servidor cortó la conexión y AFIP no muestra REC nuevos. Probá de nuevo o revisá la conexión.");
        } catch (e2) {
          toast.error("El servidor cortó la conexión. Revisá el listado por si el REC quedó emitido.");
        }
      } else {
        const detail = e.response?.data?.detail;
        let msg;
        if (typeof detail === "string") {
          msg = detail;
        } else if (Array.isArray(detail)) {
          // 422 Pydantic: extraer mensajes limpios
          msg = detail
            .map((it) => {
              const campo = (it.loc || []).filter((l) => l !== "body").join(".");
              const raw = (it.msg || "").replace(/^Value error,\s*/i, "");
              return campo ? `${campo}: ${raw}` : raw;
            })
            .join(" · ");
        } else {
          msg = e.message || "Error";
        }
        toast.error(msg);
      }
    } finally {
      setEmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="emitir-rec-dialog">
        <DialogHeader>
          <DialogTitle>
            {onCombinedSubmit
              ? "Paso 1 · Cargar REC (previo a Factura A)"
              : "Emitir Remito Electrónico Cárnico"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Formulario para emitir un Remito Electrónico Cárnico (REC) ante AFIP.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 text-sm">
          {/* Bloque 1: Cabecera */}
          <section>
            <h3 className="text-xs uppercase text-neutral-500 mb-3 tracking-widest border-b pb-1">
              Datos del REC
            </h3>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label>Punto de emisión *</Label>
                <Select
                  value={String(form.punto_emision || "")}
                  onValueChange={(v) => setForm({ ...form, punto_emision: v })}
                >
                  <SelectTrigger data-testid="rec-punto-emision-select">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {puntosEmision.length === 0 ? (
                      <div className="p-2 text-xs text-neutral-500">Cargando…</div>
                    ) : puntosEmision.map((pe) => (
                      <SelectItem key={pe.id} value={String(pe.numero)}>
                        {String(pe.numero).padStart(4, "0")}
                        {pe.descripcion ? ` · ${pe.descripcion}` : ""}
                        {pe.es_default ? "  ★" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Especie *</Label>
                <Select value={form.especie} onValueChange={(v) => setForm({ ...form, especie: v })}>
                  <SelectTrigger data-testid="rec-especie-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bovino">Bovino (995)</SelectItem>
                    <SelectItem value="porcino">Porcino (996)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Movimiento *</Label>
                <Select value={form.tipo_movimiento} onValueChange={(v) => setForm({ ...form, tipo_movimiento: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ENV">Envío directo</SelectItem>
                    <SelectItem value="TRAS">Traslado interno</SelectItem>
                    <SelectItem value="VNDPUE">Venta - Puesta a disposición</SelectItem>
                    <SelectItem value="COMPRE">Compra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoría emisor *</Label>
                <Select value={String(form.categoria_emisor)} onValueChange={(v) => setForm({ ...form, categoria_emisor: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categoriasEmisor.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Domicilio origen (solo depositario)</Label>
                <Select
                  value={String(form.cod_dom_origen || "__default__")}
                  onValueChange={(v) => setForm({ ...form, cod_dom_origen: v === "__default__" ? "" : v })}
                >
                  <SelectTrigger data-testid="rec-dom-origen">
                    <SelectValue placeholder="Del punto de emisión (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Del punto de emisión (default)</SelectItem>
                    {domsOrigen.map((d) => (
                      <SelectItem key={d.code} value={d.code}>{d.code} · {d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Bloque 2: Receptor */}
          <section>
            <h3 className="text-xs uppercase text-neutral-500 mb-3 tracking-widest border-b pb-1">
              Receptor (destino de la carga)
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Cliente *</Label>
                <ClienteCombobox
                  testId="rec-cliente-combobox"
                  placeholder="Buscar cliente por nombre o CUIT…"
                  value={clientes.find((c) => c.id === form.cliente_id) || null}
                  onSelect={(c) => setForm({ ...form, cliente_id: c?.id || "", cod_dom_destino: "" })}
                />
              </div>
              <div>
                <Label>Tipo receptor</Label>
                <Select value={form.tipo_receptor} onValueChange={(v) => setForm({ ...form, tipo_receptor: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EM">EM · Establ. matadero / con RUCA</SelectItem>
                    <SelectItem value="MI">MI · Movimiento interno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Domicilio destino (SENASA del cliente) *</Label>
                <Select value={String(form.cod_dom_destino)} onValueChange={(v) => setForm({ ...form, cod_dom_destino: v })}>
                  <SelectTrigger data-testid="rec-dom-destino"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {domsDestino.length === 0 ? (
                      <div className="p-2 text-xs text-neutral-500">
                        {!form.cliente_id
                          ? "Elegí primero un cliente"
                          : domsDestinoCuit === "invalid"
                          ? "El cliente no tiene un CUIT válido de 11 dígitos"
                          : domsDestinoLoading
                          ? "Consultando SENASA…"
                          : "SENASA no devolvió domicilios habilitados para este CUIT. Verificá que el cliente esté inscripto en el RUCA."}
                      </div>
                    ) : domsDestino.map((d) => (
                      <SelectItem key={d.code} value={d.code}>{d.code} · {d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoría receptor</Label>
                <Select
                  value={String(form.categoria_receptor)}
                  onValueChange={(v) => setForm({ ...form, categoria_receptor: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin especificar</SelectItem>
                    {categoriasReceptor.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Bloque 3: Transporte */}
          <section>
            <h3 className="text-xs uppercase text-neutral-500 mb-3 tracking-widest border-b pb-1">
              Transporte
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Transportista *</Label>
                <Select value={form.transportista_id} onValueChange={(v) => setForm({ ...form, transportista_id: v, vehiculo_id: "" })}>
                  <SelectTrigger data-testid="rec-transportista"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {transportistas.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.nombre} ({t.cuit})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehículo *</Label>
                <Select value={form.vehiculo_id} onValueChange={(v) => setForm({ ...form, vehiculo_id: v })}>
                  <SelectTrigger data-testid="rec-vehiculo"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {vehiculosFiltrados.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.dominio} {v.dominio_acoplado ? `+ ${v.dominio_acoplado}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fecha inicio viaje *</Label>
                <DateInput value={form.fecha_inicio_viaje} onChange={(e) => setForm({ ...form, fecha_inicio_viaje: e.target.value })} />
              </div>
              <div>
                <Label>Distancia (km)</Label>
                <NumberInput min="0" value={form.distancia_km} onChange={(e) => setForm({ ...form, distancia_km: e.target.value })} />
              </div>
            </div>
          </section>

          {/* Bloque 4: Mercadería */}
          <section>
            <div className="flex items-center justify-between border-b pb-1 mb-3">
              <h3 className="text-xs uppercase text-neutral-500 tracking-widest">Mercadería</h3>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="rec-add-item">
                <Plus size={14} className="mr-1" /> Agregar ítem
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase text-neutral-500">
                  <tr>
                    <th className="text-left w-10">#</th>
                    <th className="text-left">Producto SENASA *</th>
                    <th className="text-right w-24">Kilos *</th>
                    <th className="text-right w-24">Unidades</th>
                    <th className="text-right w-24">Tropa</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.mercaderias.map((m, i) => (
                    <tr key={i} className="border-b">
                      <td className="py-1 text-neutral-400">{i + 1}</td>
                      <td>
                        <Select value={String(m.cod_tipo_prod || "")} onValueChange={(v) => {
                          const tp = tiposCarne.find((tc) => tc.code === v);
                          updateItem(i, {
                            cod_tipo_prod: v,
                            descripcion: tp?.label || "",
                          });
                        }}>
                          <SelectTrigger data-testid={`rec-item-tipo-${i}`}>
                            <SelectValue placeholder="— Elegí un producto SENASA" />
                          </SelectTrigger>
                          <SelectContent className="max-h-96">
                            {tiposCarne.length === 0 ? (
                              <div className="p-3 text-xs text-neutral-500">
                                Sin tipos disponibles para esta especie
                              </div>
                            ) : (
                              // Agrupar por grupo_label
                              Object.entries(
                                tiposCarne.reduce((acc, t) => {
                                  const g = t.grupo_label || "Otros";
                                  (acc[g] ||= []).push(t);
                                  return acc;
                                }, {})
                              ).map(([grupo, items]) => (
                                <div key={grupo}>
                                  <div className="px-2 py-1 text-[10px] uppercase font-mono tracking-widest text-neutral-500 bg-neutral-50 border-b">
                                    {grupo}
                                  </div>
                                  {items.map((tc) => (
                                    <SelectItem key={tc.code} value={tc.code}>
                                      <span className="font-mono text-xs text-neutral-500 mr-2">{tc.code}</span>
                                      {tc.label}
                                    </SelectItem>
                                  ))}
                                </div>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </td>
                      <td>
                        <NumberInput step="0.001" min="0" className="text-right font-mono" value={m.kilos} onChange={(e) => updateItem(i, { kilos: e.target.value })} />
                      </td>
                      <td>
                        <NumberInput min="0" className="text-right font-mono" value={m.unidades} onChange={(e) => updateItem(i, { unidades: e.target.value })} />
                      </td>
                      <td>
                        <Input type="number" min="0" className="text-right font-mono" value={m.tropa} onChange={(e) => updateItem(i, { tropa: e.target.value })} />
                      </td>
                      <td className="text-right">
                        {form.mercaderias.length > 1 && (
                          <Button size="sm" variant="ghost" onClick={() => removeItem(i)}>
                            <Trash size={14} className="text-red-500" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-bold">
                    <td colSpan={2} className="py-2 text-right">Total kilos:</td>
                    <td className="text-right font-mono">{totalKilos.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          <div>
            <Label>Observaciones</Label>
            <Input value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            variant="secondary"
            onClick={preview}
            disabled={previewLoading}
            data-testid="rec-preview-btn"
          >
            <Eye size={14} className="mr-1" />
            {previewLoading ? "Generando…" : "Previsualizar XML"}
          </Button>
          <Button onClick={emitir} disabled={emitting} data-testid="rec-confirm-emit-btn">
            {emitting
              ? (onCombinedSubmit ? "Emitiendo REC + Factura A…" : "Emitiendo…")
              : (onCombinedSubmit ? "Emitir REC y continuar con Factura A" : "Emitir REC")}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Preview XML nested dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="rec-preview-dialog">
          <DialogHeader>
            <DialogTitle>Previsualización del envío a AFIP</DialogTitle>
            <DialogDescription className="sr-only">
              Vista previa del payload y XML que se enviará a AFIP.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-neutral-500 text-xs">
              Este es el payload que se enviaría a AFIP en la próxima llamada a
              <code className="mx-1 bg-neutral-100 px-1 rounded">generarRemito</code>.
              Todavía no fue enviado.
            </p>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs uppercase tracking-widest text-neutral-500">XML</h4>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    navigator.clipboard.writeText(previewData?.xml || "");
                    toast.success("XML copiado");
                  }}
                  data-testid="rec-copy-xml-btn"
                >
                  Copiar
                </Button>
              </div>
              <pre className="bg-neutral-950 text-emerald-100 text-xs p-4 rounded overflow-x-auto max-h-96 font-mono">
                {previewData?.xml || "(vacío)"}
              </pre>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-widest text-neutral-500 mb-2 mt-4">
                Payload (JSON)
              </h4>
              <pre className="bg-neutral-50 border text-xs p-4 rounded overflow-x-auto max-h-72 font-mono">
                {JSON.stringify(previewData?.payload || {}, null, 2)}
              </pre>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
